import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { useMatchMedia } from '~/App/shared/hooks/use-match-media';
import mediaHelper from '~/App/helpers/media-helper';
import { play, pause } from '~/App/helpers/icons';
import { mq } from '~/lib/mq';

type WrapperProps = {
  isVisible: boolean;
};

const Wrapper = styled.div<WrapperProps>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const Player = styled.video`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: block;
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
`;

const PlayButton = styled.button<{ isVisible: boolean }>`
  display: ${(props) => (props.isVisible ? 'flex' : 'none')};
  flex-direction: row;
  align-items: center;
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 2;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  pointer-events: auto;
  &:focus {
    outline: 0.125rem solid ${(props) => props.theme.hero.lightTextColor};
    outline-offset: 0.25rem;
  }
`;

const PlayButtonIcon = styled.span<{ isPlaying: boolean }>`
  color: ${(props) => props.theme.colors.white};
  height: 1.5rem;
  &:before {
    ${(props) => (props.isPlaying ? pause : play)};
    color: ${(props) => props.theme.colors.white};
    font-size: 1.5rem;
  }
`;

const PlayButtonText = styled.span`
  ${mq('<mediumLarge')`
    display: none;
  `};
  margin-left: 0.5rem;
  color: ${(props) => props.theme.colors.white};
  font-size: 1rem;
`;

type Props = {
  source: string;
  matchMedia: string;
};

export function Video({ source, matchMedia }: Props) {
  const [isVisible, setVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  const ref = useRef<HTMLVideoElement | null>(null);
  const shouldPlay = useMatchMedia(matchMedia, false);

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    shouldPlay ? ref.current.play() : ref.current.pause();
    setIsPlaying(!!shouldPlay);
    setVisible(!!shouldPlay);
  }, [shouldPlay]);

  const resetIsPlaying = () => {
    setIsPlaying(false);
  };

  useEffect(() => {
    const videoElement = ref.current;

    if (videoElement) {
      videoElement.addEventListener('ended', resetIsPlaying);

      return () => {
        videoElement.removeEventListener('ended', resetIsPlaying);
      };
    }
  }, [ref.current]);

  return (
    <Wrapper isVisible={isVisible}>
      <PlayButton
        isVisible={isVisible}
        aria-label={isPlaying ? 'Pausa video' : 'Spela video'}
        onClick={() => {
          const isPaused = ref.current?.paused;
          if (isPaused) {
            ref.current?.play();
            setIsPlaying(true);
          } else {
            ref.current?.pause();
            setIsPlaying(false);
          }
        }}
      >
        <PlayButtonIcon isPlaying={isPlaying} />
        <PlayButtonText>
          {isPlaying ? 'Pausa video' : 'Starta video'}
        </PlayButtonText>
      </PlayButton>
      <Player
        id="heroVideo"
        ref={ref}
        aria-hidden="true"
        role="presentation"
        tabIndex={-1}
        muted={true}
        playsInline={true}
      >
        <source
          src={mediaHelper.getUrl(
            source,
            {
              audio_codec: 'none',
              video_codec: 'h264'
            },
            'mp4'
          )}
          type="video/mp4"
        />
        <source
          src={mediaHelper.getUrl(
            source,
            {
              audio_codec: 'none',
              video_codec: 'vp8'
            },
            'webm'
          )}
          type="video/webm"
        />
      </Player>
    </Wrapper>
  );
}
